// frontend/src/utils/toast.js
import toastBase from 'react-hot-toast';
import { normalizeToastCall } from './toastMessage';

/**
 * Extended toast utility that wraps react-hot-toast
 * Adds info() and warning() methods that are missing from the base library
 *
 * NOTE: Dismiss behavior (close button, swipe-to-dismiss) is handled at the
 * Toaster level in index.js via DismissibleToastContent. This wrapper only
 * extends the API with custom toast types and strips status-emoji chrome so
 * every helper renders exactly one icon.
 *
 * Usage:
 *   toast('message')           - basic toast
 *   toast.success('message')   - success toast
 *   toast.error('message')     - error toast
 *   toast.info('message')      - info toast (custom)
 *   toast.warning('message')   - warning toast (custom)
 *   toast.loading('message')   - loading toast (NOT dismissable)
 */

const callToast = (method, message, options) => {
  const [nextMessage, nextOptions] = normalizeToastCall(message, options);
  return method(nextMessage, nextOptions);
};

const toast = (message, options) => callToast(toastBase, message, options);

Object.assign(toast, toastBase);

toast.success = (message, options) => callToast(toastBase.success, message, options);
toast.error = (message, options) => callToast(toastBase.error, message, options);
toast.loading = (message, options) => callToast(toastBase.loading, message, options);

toast.info = (message, options = {}) => {
  const [nextMessage, nextOptions = {}] = normalizeToastCall(message, options);
  return toastBase(nextMessage, {
    className: 'toast toast-info',
    duration: nextOptions.duration || 4000,
    ...nextOptions,
  });
};

toast.warning = (message, options = {}) => {
  const [nextMessage, nextOptions = {}] = normalizeToastCall(message, options);
  return toastBase(nextMessage, {
    className: 'toast toast-warning',
    duration: nextOptions.duration || 4000,
    ...nextOptions,
  });
};

export default toast;
